/* eslint-disable default-case */
import produce from 'immer';

import {
  types,
  Kiosk,
  KiosksState,
  KiosksActionTypes,
  CreateKioskPayload,
  CopyKioskPayload,
  UpdateKioskSchedulePayload,
  GetKiosksPending,
  GetKiosksSuccess,
  GetKiosksError,
  GetKioskPending,
  GetKioskSuccess,
  GetKioskError,
  CreateKioskPending,
  CreateKioskError,
  CopyKioskPending,
  CopyKioskError,
  UpdateKioskPayload,
  UpdateKioskPending,
  UpdateKioskSuccess,
  UpdateKioskError,
  DeleteKioskPending,
  DeleteKioskError,
  DeleteKioskSuccess,
  GetKioskSchedulePending,
  GetKioskScheduleSuccess,
  GetKioskScheduleError,
  UpdateKioskSchedulePending,
  UpdateKioskScheduleSuccess,
  UpdateKioskScheduleError,
  RebootKioskRequest,
  RebootKioskPending,
  RebootKioskError,
  ChangeStatusKioskRequest,
  ChangeStatusKioskPending,
  ChangeStatusKioskError,
  CheckAppVersionSuccess,
  CheckAppVersionPending,
  LogEventRequest,
  LogEventError,
  KioskLoginTokenRequest,
  KioskLoginTokenPending,
  KioskLoginTokenSuccess,
  KioskLoginTokenError,
  UpdateAutoRefresh,
  UpdateShowHiddenKiosks,
  UpdateKiosksSuccess,
  UpdateKiosksPending,
  UpdateKiosksError
} from './types';

// Actions
export const actions = {
  getKiosks: (payload: { exportData?: boolean }): KiosksActionTypes => ({ type: types.GET_KIOSKS.REQUEST, payload }),
  getKiosk: (payload: { kioskId: string }): KiosksActionTypes => ({ type: types.GET_KIOSK.REQUEST, payload }),
  createKiosk: (payload: CreateKioskPayload): KiosksActionTypes => ({
    type: types.CREATE_KIOSK.REQUEST,
    payload
  }),
  copyKiosk: (payload: CopyKioskPayload): KiosksActionTypes => ({
    type: types.COPY_KIOSK.REQUEST,
    payload
  }),
  updateKiosk: (payload: UpdateKioskPayload): KiosksActionTypes => ({
    type: types.UPDATE_KIOSK.REQUEST,
    payload
  }),
  updateKiosks: (payload: Partial<Kiosk>[]): KiosksActionTypes => ({
    type: types.UPDATE_KIOSKS.REQUEST,
    payload
  }),
  deleteKiosk: (payload: { id: string }): KiosksActionTypes => ({
    type: types.DELETE_KIOSK.REQUEST,
    payload
  }),
  getKioskSchedule: (payload: { kioskId: string }): KiosksActionTypes => ({
    type: types.GET_KIOSK_SCHEDULE.REQUEST,
    payload
  }),
  updateKioskSchedule: (payload: UpdateKioskSchedulePayload): KiosksActionTypes => ({
    type: types.UPDATE_KIOSK_SCHEDULE.REQUEST,
    payload
  }),
  rebootKiosk: (payload: RebootKioskRequest['payload']): KiosksActionTypes => ({
    type: types.REBOOT_KIOSK.REQUEST,
    payload
  }),
  changeStatusKiosk: (payload: ChangeStatusKioskRequest['payload']): KiosksActionTypes => ({
    type: types.CHANGE_STATUS_KIOSK.REQUEST,
    payload
  }),
  checkAppVersion: (): KiosksActionTypes => ({ type: types.CHECK_APP_VERSION.REQUEST }),
  logEvent: (payload: LogEventRequest['payload']): KiosksActionTypes => ({
    type: types.LOG_EVENT.REQUEST,
    payload
  }),
  createKioskLoginToken: (payload: KioskLoginTokenRequest['payload']): KiosksActionTypes => ({
    type: types.KIOSK_LOGIN_TOKEN.REQUEST,
    payload
  }),
  resetKiosk: (): KiosksActionTypes => ({ type: types.RESET_KIOSK }),
  resetAction: (): KiosksActionTypes => ({ type: types.RESET_ACTION }),
  resetError: (): KiosksActionTypes => ({ type: types.RESET_ERROR }),
  resetMessage: (): KiosksActionTypes => ({ type: types.RESET_MESSAGE }),
  resetLoginToken: (): KiosksActionTypes => ({ type: types.RESET_KIOSK_LOGIN_TOKEN }),
  resetExportData: (): KiosksActionTypes => ({ type: types.RESET_EXPORT_DATA }),
  updateAutoRefresh: (payload: UpdateAutoRefresh['payload']): KiosksActionTypes => ({
    type: types.UPDATE_AUTO_REFRESH,
    payload
  }),
  updateShowHiddenKiosks: (payload: UpdateShowHiddenKiosks['payload']): KiosksActionTypes => ({
    type: types.UPDATE_SHOW_HIDDEN_KIOSKS,
    payload
  })
};

// Initial State
export const initialState: KiosksState = {
  kiosks: [],
  kiosk: null,
  kioskSchedule: [],
  loginToken: null,
  version: null,
  autoRefresh: false,
  showHiddenKiosks: false,
  action: {
    success: false,
    pending: false,
    error: null,
    message: ''
  },
  export: {
    data: [],
    pending: false
  }
};

// Reducer
export const reducer = (state: KiosksState = initialState, action: KiosksActionTypes): KiosksState => {
  return produce(state, (draft) => {
    let payload;
    switch (action.type) {
      case types.GET_KIOSK.PENDING:
      case types.CREATE_KIOSK.PENDING:
      case types.COPY_KIOSK.PENDING:
      case types.UPDATE_KIOSK.PENDING:
      case types.DELETE_KIOSK.PENDING:
      case types.GET_KIOSK_SCHEDULE.PENDING:
      case types.UPDATE_KIOSK_SCHEDULE.PENDING:
      case types.REBOOT_KIOSK.PENDING:
      case types.CHANGE_STATUS_KIOSK.PENDING:
      case types.CHECK_APP_VERSION.PENDING:
      case types.KIOSK_LOGIN_TOKEN.PENDING:
      case types.UPDATE_KIOSKS.PENDING:
        draft.action.pending = (
          action as
            | GetKioskPending
            | CreateKioskPending
            | CopyKioskPending
            | UpdateKioskPending
            | DeleteKioskPending
            | GetKioskSchedulePending
            | UpdateKioskSchedulePending
            | RebootKioskPending
            | ChangeStatusKioskPending
            | CheckAppVersionPending
            | KioskLoginTokenPending
            | UpdateKiosksPending
        ).payload;
        break;
      case types.GET_KIOSKS.PENDING:
        payload = (action as GetKiosksPending).payload;
        if (payload.exportData) {
          draft.export.pending = payload.pending;
        } else {
          draft.action.pending = payload.pending;
        }
        break;
      case types.GET_KIOSKS.SUCCESS:
        payload = (action as GetKiosksSuccess).payload;
        if (payload.exportData) {
          draft.export.data = payload.kiosks;
        } else {
          draft.kiosks = payload.kiosks.map((ele: Kiosk) => {
            return { ...ele, venueName: ele.venue.name };
          });
        }
        break;
      case types.GET_KIOSK.SUCCESS:
        const payloadKiosk = (action as GetKioskSuccess).payload;
        draft.kiosk = { ...draft.kiosk, ...payloadKiosk };
        if (draft.kiosks) {
          const kioskIndex = draft.kiosks.findIndex((k) => k.id === (draft.kiosk as Kiosk).id);
          if (kioskIndex > -1) {
            Object.assign(draft.kiosks[kioskIndex], payloadKiosk);
          }
        }
        break;
      case types.CREATE_KIOSK.SUCCESS:
      case types.COPY_KIOSK.SUCCESS:
        draft.action.success = true;
        break;
      case types.DELETE_KIOSK.SUCCESS:
        const deletedId = (action as DeleteKioskSuccess).payload.id;
        draft.kiosks = draft.kiosks.filter((k) => k.id !== deletedId);
        draft.action.success = true;
        break;
      case types.UPDATE_KIOSK.SUCCESS:
        payload = (action as UpdateKioskSuccess).payload;
        draft.kiosk = { ...draft.kiosk, ...payload };
        draft.action.success = true;
        if (draft.kiosks) {
          const kiosksIndex = draft.kiosks.findIndex((k) => k.id === (draft.kiosk as Kiosk).id);
          if (kiosksIndex > -1) {
            Object.assign(draft.kiosks[kiosksIndex], payload);
          }
        }
        break;
      case types.UPDATE_KIOSKS.SUCCESS:
        const updatedKiosks = (action as UpdateKiosksSuccess).payload.kiosks;
        draft.kiosks = draft.kiosks.map((currentKiosk) => {
          const updatedKiosk = updatedKiosks.find((uk: Kiosk) => uk.id === currentKiosk.id);
          if (!updatedKiosk) return currentKiosk;
          return { ...currentKiosk, ...updatedKiosk };
        });
        draft.action.message = 'File successfully uploaded';
        break;
      case types.GET_KIOSK_SCHEDULE.SUCCESS:
        draft.kioskSchedule = (action as GetKioskScheduleSuccess).payload;
        break;
      case types.UPDATE_KIOSK_SCHEDULE.SUCCESS:
        draft.kioskSchedule = (action as UpdateKioskScheduleSuccess).payload;
        draft.action.success = true;
        break;
      case types.CHECK_APP_VERSION.SUCCESS:
        draft.version = (action as CheckAppVersionSuccess).payload;
        break;
      case types.KIOSK_LOGIN_TOKEN.SUCCESS:
        draft.loginToken = (action as KioskLoginTokenSuccess).payload;
        break;
      case types.GET_KIOSKS.ERROR:
      case types.GET_KIOSK.ERROR:
      case types.CREATE_KIOSK.ERROR:
      case types.COPY_KIOSK.ERROR:
      case types.UPDATE_KIOSK.ERROR:
      case types.GET_KIOSK_SCHEDULE.ERROR:
      case types.UPDATE_KIOSK_SCHEDULE.ERROR:
      case types.REBOOT_KIOSK.ERROR:
      case types.CHANGE_STATUS_KIOSK.ERROR:
      case types.LOG_EVENT.ERROR:
      case types.KIOSK_LOGIN_TOKEN.ERROR:
      case types.DELETE_KIOSK.ERROR:
        draft.action.error = (
          action as
            | GetKiosksError
            | GetKioskError
            | CreateKioskError
            | CopyKioskError
            | UpdateKioskError
            | GetKioskScheduleError
            | UpdateKioskScheduleError
            | RebootKioskError
            | ChangeStatusKioskError
            | LogEventError
            | KioskLoginTokenError
            | DeleteKioskError
        ).payload;
        break;
      case types.UPDATE_KIOSKS.ERROR:
        draft.action.error = (action as UpdateKiosksError).payload;
        draft.action.message = 'Error processing file';
        break;
      case types.RESET_KIOSK:
        draft.kiosk = initialState.kiosk;
        break;
      case types.RESET_ACTION:
        draft.action = initialState.action;
        break;
      case types.RESET_ERROR:
        draft.action.error = initialState.action.error;
        break;
      case types.RESET_MESSAGE:
        draft.action.message = initialState.action.message;
        break;
      case types.RESET_KIOSK_LOGIN_TOKEN:
        draft.loginToken = initialState.loginToken;
        break;
      case types.RESET_EXPORT_DATA:
        draft.export.data = initialState.export.data;
        break;
      case types.UPDATE_AUTO_REFRESH:
        draft.autoRefresh = (action as UpdateAutoRefresh).payload;
        break;
      case types.UPDATE_SHOW_HIDDEN_KIOSKS:
        draft.showHiddenKiosks = (action as UpdateShowHiddenKiosks).payload;
        break;
    }
  });
};
